import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, CalendarDays, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { BOOKING_URL, PHONE_NUMBER, PHONE_TEL } from '@/config/site';

const LOGO_URL = "/images/brand/sdl-logo-dark.png";

const navLinks = [
  { name: 'Services', path: '/services', hint: 'Treatments and guide pricing' },
  { name: 'Referrals', path: '/referrals', hint: 'Send specialist referrals' },
  { name: 'Our Team', path: '/team', hint: 'Meet our clinicians' },
  { name: 'Results', path: '/before-after', hint: 'Before and after gallery' },
  { name: 'Contact', path: '/contact', hint: 'Get in touch quickly' },
];

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomeHeroTop = location.pathname === '/' && !isScrolled && !isMobileMenuOpen;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    requestAnimationFrame(() => setIsScrolled(window.scrollY > 20));
  }, [location.pathname]);

  useEffect(() => {
    document.body.dataset.mobileMenuOpen = isMobileMenuOpen ? 'true' : 'false';
    window.dispatchEvent(new CustomEvent('mobile-menu-state', { detail: { open: isMobileMenuOpen } }));
  }, [isMobileMenuOpen]);

  useEffect(() => {
    return () => {
      delete document.body.dataset.mobileMenuOpen;
      window.dispatchEvent(new CustomEvent('mobile-menu-state', { detail: { open: false } }));
    };
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileMenuOpen]);

  const handleMobileNavClick = (event, targetPath) => {
    const isCurrentPage = location.pathname === targetPath;
    setIsMobileMenuOpen(false);
    if (!isCurrentPage) return;
    event.preventDefault();
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  };

  return (
    <>
      <header
        data-testid="header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isHomeHeroTop ? 'bg-transparent' : 'glass shadow-sm'
        }`}
      >
        <div className="container-luxury">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" data-testid="logo-link" className="flex-shrink-0">
              <img
                src={LOGO_URL}
                alt="Specialist Dental London"
                className={`h-14 w-auto transition-all duration-500 md:h-16 ${
                  isHomeHeroTop ? 'brightness-0 invert' : ''
                }`}
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`font-body text-sm tracking-wide transition-colors relative pb-1 ${
                    location.pathname === link.path
                      ? `${
                          isHomeHeroTop ? 'text-warm-white' : 'text-charcoal'
                        } font-semibold after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-accent-gold`
                      : isHomeHeroTop
                        ? 'text-warm-white/85 hover:text-warm-white'
                        : 'text-stone-600 hover:text-charcoal'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-4">
              <a
                href={PHONE_TEL}
                data-testid="header-phone-btn"
                className={`flex items-center gap-2 transition-colors ${
                  isHomeHeroTop ? 'text-accent-gold hover:text-accent-gold/85' : 'text-stone-600 hover:text-charcoal'
                }`}
              >
                <Phone size={18} strokeWidth={1.5} />
                <span className="font-body text-sm">{PHONE_NUMBER}</span>
              </a>
              <a
                href={BOOKING_URL}
                data-testid="header-book-btn"
                className="hero-book-premium premium-cta-nav inline-flex items-center justify-center"
              >
                <CalendarDays size={16} strokeWidth={1.7} className="mr-2" />
                Let's Plan Your Visit
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 lg:hidden ${isHomeHeroTop ? 'text-warm-white' : 'text-charcoal'}`}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            data-testid="mobile-menu"
            className="fixed inset-0 z-40 bg-warm-white/95 pt-20 backdrop-blur-sm lg:hidden"
          >
            <nav className="container-luxury py-5">
              <div className="flex flex-col gap-2">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.06 }}
                  >
                    <Link
                      to={link.path}
                      onClick={(event) => handleMobileNavClick(event, link.path)}
                      data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`group flex items-center justify-between rounded-2xl border px-4 py-3 transition-all duration-300 ${
                        location.pathname === link.path
                          ? 'border-accent-gold/60 bg-white shadow-[0_10px_24px_rgba(41,37,36,0.1)]'
                          : 'border-stone-200 bg-stone-50/70'
                      }`}
                    >
                      <div>
                        <p className={`font-heading text-2xl leading-none ${
                          location.pathname === link.path ? 'text-charcoal' : 'text-stone-600'
                        }`}>
                          {link.name}
                        </p>
                        <p className="mt-0.5 font-body text-xs text-stone-500">
                          {link.hint}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {location.pathname === link.path && (
                          <span className="rounded-full bg-accent-gold/20 px-2.5 py-1 font-accent text-[10px] uppercase tracking-[0.14em] text-charcoal">
                            Current
                          </span>
                        )}
                        <ChevronRight
                          size={18}
                          strokeWidth={1.6}
                          className={`transition-transform duration-300 ${
                            location.pathname === link.path ? 'text-charcoal' : 'text-stone-400 group-active:translate-x-0.5'
                          }`}
                        />
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>

              <div className="mt-7 rounded-2xl border border-stone-200 bg-white p-3.5 shadow-[0_10px_22px_rgba(41,37,36,0.08)]">
                <a
                  href={BOOKING_URL}
                  className="hero-book-premium !h-11 inline-flex w-full items-center justify-center"
                >
                  <CalendarDays size={16} strokeWidth={1.7} className="mr-2" />
                  Let's Plan Your Visit
                </a>
                <a
                  href={PHONE_TEL}
                  className="mt-2.5 inline-flex h-10 w-full items-center justify-center gap-2 rounded-full border border-stone-300 bg-stone-50 font-body text-sm text-charcoal transition-colors hover:bg-stone-100"
                >
                  <Phone size={18} strokeWidth={1.6} />
                  {PHONE_NUMBER}
                </a>
              </div>

              <div className="mt-6 border-t border-stone-200 pt-5">
                <a
                  href={PHONE_TEL}
                  className="flex items-center gap-3 text-stone-600"
                >
                  <Phone size={20} strokeWidth={1.5} />
                  <span className="font-body">{PHONE_NUMBER}</span>
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

    </>
  );
}
